import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import {
  ReturnTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import type { AddReturnTagState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { getTagTypeName } from "@redotech/redo-return-ui/flows/return-flow/add-return-tag";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { TagSelector } from "../../components/tag-selector/tag-selector";

export const AddReturnTagDetails = memo(function AddReturnTagDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<AddReturnTagState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });

  // Convert between the state's tag format and the Tag interface
  const currentTags = useMemo(
    () =>
      state.tags.map((tag) => ({
        name: tag.name,
        pillTheme: tag.color as PillTheme,
        source: ReturnTagSource.MERCHANT,
        kind: TagKind.RETURN,
      })),
    [state.tags],
  );

  const handleTagsChange = useHandler((tags: Tag[]) => {
    setState((state) => ({
      ...state,
      tags: tags.map((tag) => ({ name: tag.name, color: tag.pillTheme })),
    }));
  });

  const tagTypeName = getTagTypeName(flowType);

  return (
    <>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {tagTypeName === "Claim"
            ? t("Claim tags")
            : tagTypeName === "Warranty"
              ? t("Warranty tags")
              : t("Return tags")}
        </Text>
        <TagSelector
          displayMode="badges"
          filterSystemTags
          onTagsChange={handleTagsChange}
          readOnly={!canEditReturnsSettings}
          tags={currentTags}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {tagTypeName === "Claim"
            ? t(
                "The tags that will be added to the claim when it hits this step.",
              )
            : tagTypeName === "Warranty"
              ? t(
                  "The tags that will be added to the warranty when it hits this step.",
                )
              : t(
                  "The tags that will be added to the return when it hits this step.",
                )}
        </Text>
      </Flex>

      <Divider />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
        {flowType !== FlowType.FINALIZE_RETURN &&
          flowType !== FlowType.FINALIZE_CLAIM &&
          !state.next && (
            <Text fontSize="xs" textColor="error">
              {t("Required")}
            </Text>
          )}
      </Flex>
    </>
  );
});
